import { useEffect, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';
import { api, type GeocodeSuggestion } from '../api/endpoints';
import { useLocale } from '../i18n/locale';
import { hintStyle } from './form';

/**
 * Адрес строкой с подсказками геокодера (`AddressFields`, разд. 14).
 *
 * Подсказки — через сервер (`GET /web/geo/geocode`), а не прямым запросом к Яндексу из
 * браузера: каждое обращение к платному API проходит через кэш и счётчик бюджета
 * (FR-752, FR-755), а ключ геокодера в браузер не попадает.
 *
 * Список — ARIA-combobox: стрелки выбирают, `Enter` подставляет, `Esc` закрывает список,
 * не закрывая панель. Выбор подсказки отдаёт и строку, и точку — форма ставит метку.
 */

/** Пауза после последнего нажатия: запрос на каждую букву — это деньги и мигание списка. */
const DEBOUNCE_MS = 350;
/** Короче трёх знаков геокодер возвращает полгорода. */
const MIN_QUERY = 3;
/** Подсказок больше пяти не читают — выбирают из первых. */
const MAX_SUGGESTIONS = 5;

export function AddressField({
  id,
  value,
  onChange,
  onPick,
  maxLength,
  invalid,
  describedBy,
}: {
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onPick: (suggestion: GeocodeSuggestion) => void;
  readonly maxLength: number;
  readonly invalid: boolean;
  readonly describedBy?: string | undefined;
}): ReactElement {
  const { locale, messages } = useLocale();
  const text = messages.cabinet.map;
  const [items, setItems] = useState<readonly GeocodeSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [unavailable, setUnavailable] = useState(false);
  const [searching, setSearching] = useState(false);
  // Запрос уходит только после ввода человеком: подстановка выбранной подсказки или
  // заполнение формы правки не должны открывать список заново.
  const typed = useRef(false);

  useEffect(() => {
    const query = value.trim();

    if (!typed.current || query.length < MIN_QUERY) {
      setItems([]);
      setSearching(false);

      return undefined;
    }

    let cancelled = false;
    setSearching(true);

    const timer = window.setTimeout(() => {
      api
        .geocode(query, locale)
        .then((result) => {
          if (!cancelled) {
            setItems(result.slice(0, MAX_SUGGESTIONS));
            setActive(-1);
            setOpen(true);
            setUnavailable(false);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setItems([]);
            setUnavailable(true);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setSearching(false);
          }
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [value, locale]);

  const pick = (suggestion: GeocodeSuggestion): void => {
    typed.current = false;
    setOpen(false);
    setItems([]);
    onPick(suggestion);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (!open || items.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => (current + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => (current <= 0 ? items.length - 1 : current - 1));
    } else if (event.key === 'Enter' && active >= 0) {
      event.preventDefault();
      const item = items[active];

      if (item !== undefined) {
        pick(item);
      }
    } else if (event.key === 'Escape') {
      // Esc закрывает список, а не панель с формой.
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      setOpen(false);
    }
  };

  const listId = `${id}-suggestions`;
  const expanded = open && items.length > 0;

  return (
    <div style={{ position: 'relative', display: 'grid', gap: 'var(--sp-1)', minWidth: 0 }}>
      <input
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-activedescendant={expanded && active >= 0 ? `${listId}-${active}` : undefined}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        autoComplete="off"
        maxLength={maxLength}
        value={value}
        onChange={(event) => {
          typed.current = true;
          onChange(event.target.value);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => window.setTimeout(() => setOpen(false), BLUR_DELAY_MS)}
        onFocus={() => setOpen(items.length > 0)}
      />

      <ul
        id={listId}
        role="listbox"
        aria-label={text.suggestions}
        hidden={!expanded}
        style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 5,
          margin: 'var(--sp-1) 0 0',
          padding: 'var(--sp-1)',
          listStyle: 'none',
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-sm)',
          boxShadow: 'var(--shadow-pop)',
        }}
      >
        {items.map((item, index) => (
          <li
            key={`${item.formatted}-${index}`}
            id={`${listId}-${index}`}
            role="option"
            aria-selected={index === active}
            // mousedown, а не click: click приходит после blur, когда список уже закрыт.
            onMouseDown={(event) => {
              event.preventDefault();
              pick(item);
            }}
            onMouseEnter={() => setActive(index)}
            style={{
              padding: 'var(--sp-2) var(--sp-3)',
              borderRadius: 'var(--r-xs)',
              cursor: 'pointer',
              minHeight: 'var(--field-h)',
              display: 'grid',
              alignItems: 'center',
              background: index === active ? 'var(--accent-soft)' : 'transparent',
              color: index === active ? 'var(--accent-ink)' : 'var(--text)',
            }}
          >
            <span>{item.formatted}</span>
            {item.precision === 'exact' ? null : <span style={hintStyle}>{text.approximate}</span>}
          </li>
        ))}
      </ul>

      {searching ? (
        <span role="status" style={hintStyle}>
          {text.searching}
        </span>
      ) : unavailable ? (
        <span role="status" style={hintStyle}>
          {text.geocodeUnavailable}
        </span>
      ) : open && typed.current && items.length === 0 && value.trim().length >= MIN_QUERY ? (
        <span role="status" style={hintStyle}>
          {text.nothingFound}
        </span>
      ) : null}
    </div>
  );
}

/** Сколько ждать перед закрытием списка после потери фокуса: успеть выбрать мышью. */
const BLUR_DELAY_MS = 150;
